import { useLayoutEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { HAL, useIndeling } from './indeling'
import { useAnimatie, useLicht } from '../scene/store'
import { OMLOOP, halklok, rondOm } from './omloop'

/**
 * Het stof in de lichtbundels drijft door de ruis: 0,067 per seconde, als een
 * cirkel die één keer rond is per omloop van de hal (`omloop.ts`). Eerst een
 * rechte lijn, maar die komt nooit terug waar hij begon — en de film van de
 * zweefronde moet naadloos rondlopen.
 */
const DRIFT = { straal: (0.067 * OMLOOP) / (2 * Math.PI), w: (2 * Math.PI) / OMLOOP }
/** De haperende armatuur, op frequenties die rondgaan per omloop. */
const HAPER = [rondOm(9), rondOm(9 * 0.37), rondOm(9 * 2.1)]

/** Een vast getal 0–1 per tijdvak: dezelfde hapering in de film als in de hal. */
const toeval = (n: number) => {
  const x = Math.sin(n * 12.9898) * 43758.5453
  return x - Math.floor(x)
}

/** Hoogte van de onderkant van de armaturen. */
const ARMATUUR_Y = HAL.hoogte - 0.95
/** Lineaire high-bay: 2,4 m lang, om de 6 m boven het gangpad. */
const LENGTE = 2.4
const AFSTAND = 6

/**
 * Zachte lichtkegel. Geen doorschijnende driehoek maar een kegel die aan de
 * rand vervaagt (hoe schuiner je op het oppervlak kijkt, hoe minder licht er
 * door de "dikte" van de bundel komt), van boven naar beneden uitdooft, en
 * waar stof in drijft.
 *
 * Twee dingen voorkomen de typische nep-bundel: hij snijdt de vloer niet met
 * een harde rand (de onderste 20% loopt naar nul), en hij verdwijnt als de
 * camera er dichtbij of in staat — van binnenuit zie je een bundel niet.
 */
const bundelVertex = /* glsl */ `
varying vec3 vWereld;
varying vec3 vNormaal;
varying float vHoogte;
varying float vDiepte;
void main() {
  vec4 w = modelMatrix * instanceMatrix * vec4(position, 1.0);
  vWereld = w.xyz;
  vNormaal = normalize(mat3(modelMatrix * instanceMatrix) * normal);
  vHoogte = uv.y;
  vec4 mv = viewMatrix * w;
  vDiepte = -mv.z;
  gl_Position = projectionMatrix * mv;
}
`

const bundelFragment = /* glsl */ `
uniform vec3 kleur;
uniform float sterkte;
uniform vec3 drift;
uniform float mistNabij;
uniform float mistVer;
varying vec3 vWereld;
varying vec3 vNormaal;
varying float vHoogte;
varying float vDiepte;

float hash13(vec3 p) {
  p = fract(p * 0.1031);
  p += dot(p, p.zyx + 31.32);
  return fract((p.x + p.y) * p.z);
}
float ruis(vec3 p) {
  vec3 i = floor(p); vec3 f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(hash13(i), hash13(i + vec3(1,0,0)), f.x), mix(hash13(i + vec3(0,1,0)), hash13(i + vec3(1,1,0)), f.x), f.y),
             mix(mix(hash13(i + vec3(0,0,1)), hash13(i + vec3(1,0,1)), f.x), mix(hash13(i + vec3(0,1,1)), hash13(i + vec3(1,1,1)), f.x), f.y), f.z);
}

void main() {
  vec3 naarCamera = cameraPosition - vWereld;
  float afstand = length(naarCamera);
  float frontaal = abs(dot(normalize(vNormaal), naarCamera / afstand));
  float rand = pow(frontaal, 2.2);
  float lengte = smoothstep(0.0, 0.2, vHoogte) * mix(0.25, 1.0, vHoogte * vHoogte);
  float stof = 0.65 + 0.7 * ruis(vWereld * vec3(0.9, 0.35, 0.9) + drift);
  float dichtbij = smoothstep(1.2, 6.0, afstand);
  float mist = 1.0 - smoothstep(mistNabij, mistVer * 1.2, vDiepte);
  float a = sterkte * rand * lengte * stof * dichtbij * mist;
  gl_FragColor = vec4(kleur * a, 1.0);
}
`

/**
 * Anamorfe lensstreep: een horizontale streep door elk fel lichtpunt, altijd
 * horizontaal in beeld, ongeacht hoe het armatuur hangt. Een anamorfe lens
 * doet dat door zijn cilindrische element, en het is misschien wel het
 * herkenbaarste "film"-signaal dat er bestaat.
 *
 * Het vlak wordt in clipruimte opgespannen: het midden komt uit de wereld, de
 * breedte is een vaste fractie van het beeld.
 */
const streepVertex = /* glsl */ `
uniform float breedte;
uniform float aspect;
varying vec2 vUv;
varying float vZicht;
void main() {
  vec4 midden = projectionMatrix * viewMatrix * modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
  vUv = uv * 2.0 - 1.0;
  // Achter de camera of ver buiten beeld: niets tekenen.
  vec2 ndc = midden.xy / max(midden.w, 1e-4);
  vZicht = midden.w > 0.0 ? (1.0 - smoothstep(0.9, 1.25, max(abs(ndc.x), abs(ndc.y)))) : 0.0;
  // Verre lampen krijgen een kortere streep: het is licht, geen sticker.
  float schaal = clamp(14.0 / midden.w, 0.25, 1.0);
  vec2 maat = vec2(breedte * schaal, breedte * schaal * 0.022 * aspect);
  gl_Position = midden + vec4(position.xy * maat * midden.w, 0.0, 0.0);
}
`

const streepFragment = /* glsl */ `
uniform vec3 kleur;
uniform float sterkte;
varying vec2 vUv;
varying float vZicht;
void main() {
  float x = abs(vUv.x);
  float y = abs(vUv.y);
  float staart = pow(1.0 - x, 3.0) * exp(-y * y * 6.0);
  float kern = exp(-(x * x * 900.0 + y * y * 5.0));
  float a = (staart * 0.55 + kern * 1.4) * sterkte * vZicht;
  gl_FragColor = vec4(kleur * a, 1.0);
}
`

/** Zachte, langwerpige lichtvlek als textuur: goedkoper dan een shader per vlek. */
function vlekTextuur() {
  const n = 64
  const data = new Uint8Array(new ArrayBuffer(n * n * 4))
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const u = (x / (n - 1)) * 2 - 1
      const v = (y / (n - 1)) * 2 - 1
      const r2 = u * u + v * v
      const a = Math.max(0, Math.exp(-r2 * 3.2) - Math.exp(-3.2)) / (1 - Math.exp(-3.2))
      const i = (y * n + x) * 4
      data[i] = data[i + 1] = data[i + 2] = Math.round(a * 255)
      data[i + 3] = 255
    }
  }
  const t = new THREE.DataTexture(data, n, n, THREE.RGBAFormat)
  t.needsUpdate = true
  t.magFilter = THREE.LinearFilter
  t.minFilter = THREE.LinearFilter
  return t
}

/**
 * De armaturen boven de gangpaden: lineaire LED-high-bays van 2,4 m, om de
 * zes meter, aan twee staaldraden. Met per armatuur een zachte bundel, een
 * lichtvlek op de vloer en een lensstreep.
 *
 * Het waren er drie per gangpad, elk 4,4 m lang. Echte hallen hangen er veel
 * meer en veel kleinere, en juist die herhaling is wat diepte geeft: een rij
 * lampen die in het gangpad naar het verdwijnpunt loopt, is de eenvoudigste
 * manier om 50 meter hal ook als 50 meter te laten lezen.
 *
 * Alles is instanced: vijf draw calls voor 45 armaturen.
 */
export function Armaturen() {
  const ind = useIndeling()
  const licht = useLicht()
  const anim = useAnimatie()

  const behuizing = useRef<THREE.InstancedMesh>(null)
  const kap = useRef<THREE.InstancedMesh>(null)
  const diffusor = useRef<THREE.InstancedMesh>(null)
  const draden = useRef<THREE.InstancedMesh>(null)
  const bundels = useRef<THREE.InstancedMesh>(null)
  const vlekken = useRef<THREE.InstancedMesh>(null)
  const strepen = useRef<THREE.InstancedMesh>(null)

  const posities = useMemo(() => {
    const uit: [number, number][] = []
    const halveStelling = (ind.cfg.bays * 2.7) / 2
    for (let i = 0; i < ind.cfg.gangpaden; i++) {
      for (let x = -halveStelling + 3; x <= halveStelling - 2; x += AFSTAND) uit.push([x, ind.gangpadZ(i)])
    }
    return uit
  }, [ind])

  // Welk armatuur hapert. Eén, en niet vooraan: een flikkerende lamp in de
  // verte maakt een ruimte echt; vlak voor de camera wordt het een storing.
  const flikkerIndex = Math.min(posities.length - 1, Math.floor(posities.length * 0.62))

  const vlekMap = useMemo(() => vlekTextuur(), [])

  const bundelMateriaal = useMemo(
    () => new THREE.ShaderMaterial({
      vertexShader: bundelVertex,
      fragmentShader: bundelFragment,
      uniforms: {
        kleur: { value: new THREE.Color() },
        sterkte: { value: 0.05 },
        drift: { value: new THREE.Vector3() },
        mistNabij: { value: 30 },
        mistVer: { value: 120 },
      },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    }),
    [],
  )

  const streepMateriaal = useMemo(
    () => new THREE.ShaderMaterial({
      vertexShader: streepVertex,
      fragmentShader: streepFragment,
      uniforms: {
        kleur: { value: new THREE.Color('#7fa6ff') },
        sterkte: { value: 1 },
        breedte: { value: 0.32 },
        aspect: { value: 16 / 9 },
      },
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
    }),
    [],
  )

  useLayoutEffect(() => () => { bundelMateriaal.dispose(); streepMateriaal.dispose(); vlekMap.dispose() },
    [bundelMateriaal, streepMateriaal, vlekMap])

  // Kleuren en sterktes. De diffusor is HDR: veel feller dan 1, zodat bloom en
  // de tone mapping er een lamp van maken in plaats van een witte balk.
  const diffusorKleur = useMemo(
    () => new THREE.Color(licht.armatuurKleur).multiplyScalar(9 * licht.armatuurSterkte),
    [licht.armatuurKleur, licht.armatuurSterkte],
  )

  useLayoutEffect(() => {
    bundelMateriaal.uniforms.kleur.value.set(licht.armatuurKleur)
    bundelMateriaal.uniforms.sterkte.value = 0.07 * licht.bundelSterkte * licht.armatuurSterkte
    bundelMateriaal.uniforms.mistNabij.value = licht.mistNabij
    bundelMateriaal.uniforms.mistVer.value = licht.mistVer
    streepMateriaal.uniforms.sterkte.value = 0.55 * licht.armatuurSterkte
  }, [licht, bundelMateriaal, streepMateriaal])

  useLayoutEffect(() => {
    const d = new THREE.Object3D()
    const zet = (mesh: THREE.InstancedMesh | null, i: number) => {
      if (!mesh) return
      d.updateMatrix()
      mesh.setMatrixAt(i, d.matrix)
    }
    posities.forEach(([x, z], i) => {
      d.rotation.set(0, 0, 0)
      d.position.set(x, ARMATUUR_Y + 0.07, z); d.scale.set(LENGTE, 0.1, 0.34); zet(behuizing.current, i)
      d.position.set(x, ARMATUUR_Y + 0.15, z); d.scale.set(LENGTE - 0.1, 0.06, 0.22); zet(kap.current, i)
      d.position.set(x, ARMATUUR_Y + 0.012, z); d.scale.set(LENGTE - 0.08, 0.02, 0.26); zet(diffusor.current, i)

      // Twee staaldraden tot aan het dak.
      const draad = HAL.hoogte - ARMATUUR_Y - 0.18
      for (const [j, dx] of [[0, -LENGTE * 0.38], [1, LENGTE * 0.38]] as const) {
        d.position.set(x + dx, ARMATUUR_Y + 0.18 + draad / 2, z); d.scale.set(1, draad, 1)
        zet(draden.current, i * 2 + j)
      }

      // Bundel: van de diffusor tot de vloer, langwerpig in de lengte van de lamp.
      d.position.set(x, ARMATUUR_Y / 2, z); d.scale.set(1.35, 1, 0.85); zet(bundels.current, i)

      // Vlek op de vloer.
      d.position.set(x, 0.018, z); d.rotation.set(-Math.PI / 2, 0, 0); d.scale.set(LENGTE + 5.5, 3.6, 1)
      zet(vlekken.current, i)
      d.rotation.set(0, 0, 0)

      d.position.set(x, ARMATUUR_Y - 0.01, z); d.scale.set(1, 1, 1); zet(strepen.current, i)
    })
    for (const m of [behuizing, kap, diffusor, draden, bundels, vlekken, strepen]) {
      if (!m.current) continue
      m.current.instanceMatrix.needsUpdate = true
      m.current.computeBoundingSphere()
    }
    // De diffusors krijgen een instantiekleur, zodat de haperende lamp per
    // frame één kleur kan wijzigen in plaats van een eigen mesh te hebben.
    if (diffusor.current) {
      const wit = new THREE.Color(1, 1, 1)
      posities.forEach((_, i) => diffusor.current!.setColorAt(i, wit))
      if (diffusor.current.instanceColor) diffusor.current.instanceColor.needsUpdate = true
    }
  }, [posities, licht.bundels, licht.lichtvlekken, licht.lensStrepen])

  const kleurTmp = useMemo(() => new THREE.Color(), [])

  useFrame((state) => {
    const t = halklok.t
    const a = t * DRIFT.w
    bundelMateriaal.uniforms.drift.value.set(0, DRIFT.straal * Math.sin(a), DRIFT.straal * Math.cos(a))
    streepMateriaal.uniforms.aspect.value = state.size.width / Math.max(1, state.size.height)

    // Eén armatuur hapert: een defecte driver die af en toe wegvalt.
    const m = diffusor.current
    if (!m || !anim.aan) return
    const storing = Math.sin(t * HAPER[0]) * Math.sin(t * HAPER[1]) * Math.sin(t * HAPER[2])
    const niveau = storing > 0.55 ? 0.12 + toeval(Math.floor((t % OMLOOP) * 30)) * 0.4 : 1
    m.setColorAt(flikkerIndex, kleurTmp.setScalar(niveau))
    if (m.instanceColor) m.instanceColor.needsUpdate = true
  })

  const n = posities.length

  return (
    <group>
      <instancedMesh ref={behuizing} args={[undefined, undefined, n]} castShadow>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#9aa1aa" roughness={0.38} metalness={0.85} />
      </instancedMesh>
      <instancedMesh ref={kap} args={[undefined, undefined, n]} castShadow>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#5d646d" roughness={0.5} metalness={0.7} />
      </instancedMesh>
      <instancedMesh ref={diffusor} args={[undefined, undefined, n]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshBasicMaterial color={diffusorKleur} toneMapped={false} />
      </instancedMesh>
      <instancedMesh ref={draden} args={[undefined, undefined, n * 2]}>
        <cylinderGeometry args={[0.004, 0.004, 1, 4]} />
        <meshStandardMaterial color="#8d949c" roughness={0.4} metalness={0.9} />
      </instancedMesh>

      {licht.bundels && (
        <instancedMesh ref={bundels} args={[undefined, bundelMateriaal, n]} frustumCulled={false} renderOrder={2}>
          <cylinderGeometry args={[0.2, 2.6, ARMATUUR_Y, 28, 1, true]} />
        </instancedMesh>
      )}

      {licht.lichtvlekken && (
        <instancedMesh ref={vlekken} args={[undefined, undefined, n]} renderOrder={1}>
          <planeGeometry args={[1, 1]} />
          <meshBasicMaterial
            map={vlekMap}
            color={new THREE.Color(licht.armatuurKleur).multiplyScalar(0.16 * licht.armatuurSterkte)}
            transparent
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            polygonOffset
            polygonOffsetFactor={-2}
          />
        </instancedMesh>
      )}

      {licht.lensStrepen && (
        <instancedMesh ref={strepen} args={[undefined, streepMateriaal, n]} frustumCulled={false} renderOrder={3}>
          <planeGeometry args={[2, 2]} />
        </instancedMesh>
      )}
    </group>
  )
}
